import { Bookmark, ChevronDown, Flag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  LIFECYCLE_DESCRIPTIONS,
  LIFECYCLE_LABELS,
  lifecycleStateOf,
} from "@/constants/product";
import type { ProductLifecycleState } from "@/types/product";

interface LifecycleStateBadgeProps {
  /**
   * The **raw** mirror field, passed straight through from wherever the row came
   * from. This component normalises it itself, which is what makes it the
   * normalisation boundary for every rendering reader
   * (`components/product.md § The Products page — four surfaces, one write path`,
   * rule 1). `strictNullChecks` is off, so nothing in the compiler would catch a
   * missed `?? "unassigned"` at a call site.
   */
  state?: ProductLifecycleState;
  /** When provided, the hover key shows a "Change lifecycle state" link that calls this. */
  onChangeRequest?: () => void;
  /** Hide the hover key (e.g. inside the dialog, where descriptions are already shown). */
  disableTooltip?: boolean;
  className?: string;
}

/**
 * Every state appears in the hover key now. Until AUT-435's 2026-10-07 revision `unassigned` was in
 * neither the key nor the page: it rendered nothing at all. Amber's note is that a user could not
 * see that a product had no designation, and had nowhere to assign one from where they already
 * were -- so `unassigned` became a tag of its own, and the key has to explain all three.
 */
const KEYED_STATES = ["priority", "core", "unassigned"] as const;

/**
 * The dashed outline the `unassigned` tag wears. Not a `Badge` variant: every variant is a filled
 * pill, and this is the one tag in the set that reads as an empty slot rather than a label.
 */
const UNASSIGNED_TAG_CLASSES =
  "inline-flex items-center gap-1 whitespace-nowrap rounded border border-dashed " +
  "border-muted-foreground/50 bg-muted/50 px-2.5 py-0.5 text-xs font-medium text-foreground";

/**
 * Badge for the manual product lifecycle designation (Priority / Core / Unassigned).
 *
 * `unassigned` renders a dashed, clickable tag **when there is something to click** — i.e. when
 * `onChangeRequest` is passed. A viewer is passed none and still sees nothing, which is the rule
 * that has not changed: the tag is an affordance, and an affordance a viewer cannot use is clutter
 * that implies a permission they lack.
 *
 * Hovering shows a key explaining all three states; when `onChangeRequest` is passed the key also
 * offers a second route into the set-state dialog (`intent.md § FR-8a`), and the badge itself
 * becomes the first route.
 *
 * Separate concept from the Product Families hero designation.
 */
export function LifecycleStateBadge({
  state,
  onChangeRequest,
  disableTooltip,
  className,
}: LifecycleStateBadgeProps) {
  const resolved = lifecycleStateOf(state);
  const clickable = !disableTooltip && !!onChangeRequest;

  if (resolved === "unassigned") {
    // Inside the key and the dialog the tag is a legend entry, never a control.
    if (disableTooltip) {
      return <span className={cn(UNASSIGNED_TAG_CLASSES, className)}>{LIFECYCLE_LABELS.unassigned}</span>;
    }
    // Nothing to offer a viewer: no tag rather than a dead one.
    if (!onChangeRequest) return null;
  }

  const isPriority = resolved === "priority";
  const Icon = isPriority ? Flag : Bookmark;

  const badge =
    resolved === "unassigned" ? (
      <span
        className={cn(
          UNASSIGNED_TAG_CLASSES,
          "cursor-pointer pr-1.5 transition-colors hover:border-primary hover:bg-primary-tint-soft",
          className,
        )}
      >
        {LIFECYCLE_LABELS.unassigned}
        <ChevronDown className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2} aria-hidden />
      </span>
    ) : (
      <Badge
        variant={isPriority ? "warning" : "info"}
        interactive={clickable}
        className={cn("gap-1 whitespace-nowrap", !disableTooltip && !onChangeRequest && "cursor-default", className)}
      >
        <Icon className="h-3 w-3" strokeWidth={2} />
        {LIFECYCLE_LABELS[resolved]}
      </Badge>
    );

  if (disableTooltip) return badge;

  // No `TooltipProvider` here — `constraints.md § C-31(b)`: a nested provider resets
  // rather than inherits. `delayDuration` rides on this `<Tooltip>` instead, which is
  // what C-31(a) prescribes, and `@radix-ui/react-tooltip@1.2.7` declares the prop on
  // `TooltipProps` ("This will override the prop with the same name passed to Provider").
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span
          className="inline-flex"
          role={onChangeRequest ? "button" : undefined}
          tabIndex={onChangeRequest ? 0 : undefined}
          aria-label={onChangeRequest ? `Change lifecycle state (${LIFECYCLE_LABELS[resolved]})` : undefined}
          onClick={(e) => {
            // Always stop: the badge sits inside a clickable row and a clickable card, and a click
            // here must never navigate, whether or not it also opens the dialog.
            e.stopPropagation();
            onChangeRequest?.();
          }}
          onKeyDown={(e) => {
            if (onChangeRequest && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              e.stopPropagation();
              onChangeRequest();
            }
          }}
        >
          {badge}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-2">
          {KEYED_STATES.map((keyedState) => (
            <div key={keyedState} className="contents">
              <LifecycleStateBadge state={keyedState} disableTooltip className="justify-self-start rounded" />
              <p className="pt-0.5">{LIFECYCLE_DESCRIPTIONS[keyedState]}</p>
            </div>
          ))}
        </div>
        {onChangeRequest && (
          <div className="mt-2 flex justify-end border-t border-border pt-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onChangeRequest();
              }}
              className="font-medium text-primary hover:underline"
            >
              Change lifecycle state
            </button>
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
